'use client';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  SidebarContent as ShadSidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarInput,
  SidebarFooter,
  Sidebar,
  SidebarHeader,
} from '@/components/ui/sidebar';
import {
  LayoutDashboard,
  Settings,
  Map,
  BookOpen,
  FolderKanban,
  MessageSquare,
  FileText,
  Search,
} from 'lucide-react';
import { useSidebar } from '@/components/ui/sidebar';

// Custom Ankh SVG Icon
const AnkhIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5" // Slightly thinner stroke
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-6 w-6 mr-2 text-sidebar-primary flex-shrink-0" // Adjusted size and margin
  >
    <path d="M12 6a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" /> {/* Loop */}
    <path d="M12 14v8" /> {/* Vertical Line */}
    <path d="M7 18h10" /> {/* Horizontal Line */}
  </svg>
);

const SidebarNavigation = () => {
  const { state } = useSidebar(); // Get sidebar state

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border pb-3 pt-3 transition-colors duration-200">
        <div className="flex items-center justify-center group-data-[state=collapsed]/sidebar:justify-center">
          <AnkhIcon />
          <div className="flex flex-col group-data-[state=expanded]/sidebar:opacity-100 group-data-[state=collapsed]/sidebar:opacity-0 transition-opacity duration-200">
            <h2 className="text-xl font-bold text-sidebar-primary whitespace-nowrap transition-colors duration-200">VeteranOS</h2>
            <h3 className="text-xs text-sidebar-foreground/80 tracking-wide whitespace-nowrap transition-colors duration-200">
              Veteran Transition OS
            </h3>
          </div>
        </div>
        {/* Subtle accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-sidebar-primary/30 to-transparent" />
      </SidebarHeader>
      <ShadSidebarContent>
        {' '}
        {/* Use the renamed import */}
        {/* Search Input */}
        <div className="px-3 py-2 relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-sidebar-foreground/60 group-data-[state=collapsed]/sidebar:left-1/2 group-data-[state=collapsed]/sidebar:-translate-x-1/2 transition-colors duration-200" />
          <SidebarInput
            placeholder={state === 'expanded' ? 'Search resources...' : ''}
            aria-label="Search resources"
            className="pl-8 bg-sidebar-background/50 border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/50 focus-visible:ring-sidebar-ring group-data-[state=collapsed]/sidebar:w-9 group-data-[state=collapsed]/sidebar:h-9 group-data-[state=collapsed]/sidebar:rounded-full group-data-[state=collapsed]/sidebar:pl-2 transition-all duration-200"
          />
        </div>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton 
                to="/" 
                className="group relative transition-all duration-200 hover:bg-sidebar-accent data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-sidebar-primary opacity-0 group-data-[active=true]:opacity-100 transition-opacity duration-200" />
                <LayoutDashboard className="mr-2 h-4 w-4 transition-colors duration-200" />
                <span className="transition-colors duration-200">Dashboard</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton 
                to="/skill-map"
                className="group relative transition-all duration-200 hover:bg-sidebar-accent data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-sidebar-primary opacity-0 group-data-[active=true]:opacity-100 transition-opacity duration-200" />
                <Map className="mr-2 h-4 w-4 transition-colors duration-200" />
                <span className="transition-colors duration-200">Skill Map</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton 
                to="/learning-plan"
                className="group relative transition-all duration-200 hover:bg-sidebar-accent data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-sidebar-primary opacity-0 group-data-[active=true]:opacity-100 transition-opacity duration-200" />
                <BookOpen className="mr-2 h-4 w-4 transition-colors duration-200" />
                <span className="transition-colors duration-200">Learning Plan</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton 
                to="/projects"
                className="group relative transition-all duration-200 hover:bg-sidebar-accent data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-sidebar-primary opacity-0 group-data-[active=true]:opacity-100 transition-opacity duration-200" />
                <FolderKanban className="mr-2 h-4 w-4 transition-colors duration-200" />
                <span className="transition-colors duration-200">Projects</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton 
                to="/transition-agent"
                className="group relative transition-all duration-200 hover:bg-sidebar-accent data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-sidebar-primary opacity-0 group-data-[active=true]:opacity-100 transition-opacity duration-200" />
                <MessageSquare className="mr-2 h-4 w-4 transition-colors duration-200" />
                <span className="transition-colors duration-200">Transition Agent</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton 
                to="/resume-brief"
                className="group relative transition-all duration-200 hover:bg-sidebar-accent data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-sidebar-primary opacity-0 group-data-[active=true]:opacity-100 transition-opacity duration-200" />
                <FileText className="mr-2 h-4 w-4 transition-colors duration-200" />
                <span className="transition-colors duration-200">Resume & Brief</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton 
                to="/settings"
                className="group relative transition-all duration-200 hover:bg-sidebar-accent data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground data-[active=true]:font-medium"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-sidebar-primary opacity-0 group-data-[active=true]:opacity-100 transition-opacity duration-200" />
                <Settings className="mr-2 h-4 w-4 transition-colors duration-200" />
                <span className="transition-colors duration-200">Settings</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </ShadSidebarContent>{' '}
      {/* Use the renamed import */}
      <SidebarFooter className="mt-auto border-t border-sidebar-border pt-3 pb-3 group-data-[state=collapsed]/sidebar:hidden transition-colors duration-200 relative">
        {/* Subtle accent line at top */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-sidebar-primary/20 to-transparent" />
        <p className="text-[10px] text-sidebar-foreground/60 text-center tracking-wide transition-colors duration-200">
          Built by a veteran for veterans
        </p>
        <p className="text-[10px] text-sidebar-foreground/40 text-center transition-colors duration-200">
          © {new Date().getFullYear()} VeteranOS
        </p>
      </SidebarFooter>
    </Sidebar>
  );
};

export default SidebarNavigation;
